<template>
  <div id="content-wrapper" class="nhat-ky-otp otp-scope">
    <div class="otp-page-head">
      <h5 class="otp-page-title">Nhật ký OTP</h5>
      <p class="otp-page-sub">Tra cứu và xuất lịch sử xác thực, quản lý thiết bị và các sự kiện OTP.</p>
    </div>
    <b-form-group class="border-bottom-form otp-filter-bar">
      <!-- 7 filter chia 2 hàng cho vừa 12 cột grid.
           Hàng 1: user/device/from/to = 3+3+3+3.
           Hàng 2: result/eventTypeGroup/reason = 3+3+6. -->
      <div class="row">
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Người dùng</label>
          <b-form-input
            placeholder="Tên đăng nhập"
            type="text"
            v-model="req.filterBy"
            @keyup.enter="search"
          />
        </div>
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Thiết bị</label>
          <b-form-input
            placeholder="Tên hoặc mã thiết bị"
            type="text"
            v-model="req.deviceFilter"
            @keyup.enter="search"
          />
        </div>
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Từ ngày</label>
          <b-form-input type="date" v-model="req.from" />
        </div>
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Đến ngày</label>
          <b-form-input type="date" v-model="req.to" />
        </div>
      </div>
      <div class="row">
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Kết quả</label>
          <b-form-select v-model="req.result" :options="resultOptions" />
        </div>
        <div class="col-md-3 col-xs-12 mb-2">
          <label>Nhóm loại</label>
          <b-form-select v-model="req.eventTypeGroup" :options="eventTypeGroupOptionsFinal" />
        </div>
        <div class="col-md-6 col-xs-12 mb-2">
          <label>Lý do</label>
          <b-form-select v-model="req.reason" :options="reasonOptionsFinal" />
        </div>
      </div>
      <div class="d-flex mt-2">
        <base-button button="Tìm kiếm" @click="search()" />
        <base-button class="ml-2" button="Xuất Excel" @click="exportFile('xlsx')" />
        <base-button class="ml-2" button="Xuất PDF" @click="exportFile('pdf')" />
      </div>
    </b-form-group>

    <p class="color-gray-1">Tìm thấy {{ totalRows }} kết quả</p>
    <DataTable :loading="isLoadingTable" :value="items">
      <Column header="STT" headerStyle="width: 50px" bodyStyle="text-align: center">
        <template #body="slotProps">
          <span>{{ (req.PageIndex - 1) * req.PageSize + slotProps.index + 1 }}</span>
        </template>
      </Column>
      <Column header="Người dùng" bodyStyle="text-align: left">
        <template #body="slotProps"><span>{{ slotProps.data.userName }}</span></template>
      </Column>
      <Column header="Thiết bị" bodyStyle="text-align: left">
        <template #body="slotProps"><span>{{ slotProps.data.deviceName || slotProps.data.deviceId }}</span></template>
      </Column>
      <Column header="Kết quả" headerStyle="width: 120px">
        <template #body="slotProps">
          <span class="otp-chip" :class="slotProps.data.result ? 'otp-chip--on' : 'otp-chip--off'">
            <span class="otp-chip__dot" aria-hidden="true"></span>
            {{ slotProps.data.result ? "Thành công" : "Thất bại" }}
          </span>
        </template>
      </Column>
      <Column header="Lý do">
        <template #body="slotProps"><span>{{ reasonLabel(slotProps.data.reason) }}</span></template>
      </Column>
      <Column header="IP" headerStyle="width: 140px">
        <template #body="slotProps"><span class="otp-mono">{{ slotProps.data.ip }}</span></template>
      </Column>
      <Column header="Thời gian" headerStyle="width: 180px">
        <template #body="slotProps"><span class="otp-mono">{{ formatDate(slotProps.data.createdAt) }}</span></template>
      </Column>
      <template #empty><div class="text-center">Không có dữ liệu</div></template>
      <template #loading><div class="text-center">Đang tải dữ liệu ...</div></template>
    </DataTable>

    <BasePagination
      class="mt-3"
      :current-page="req.PageIndex"
      :page-size-prop="req.PageSize"
      :total-pages="totalPages"
      :total-rows="totalRows"
      @changeNum="onChangeNum($event)"
      @changePages="onChangePages($event)"
    />
  </div>
</template>

<script>
import BasePagination from "@/components/common/BasePagination";
import moment from "moment";

// Phase 8 UC17: màn xem/lọc/xuất nhật ký OTP. Resolve response.data (ServiceResponse camelCase) -> data = PagedResult.
export default {
  name: "DanhSachLogOtp",
  components: { BasePagination },
  data() {
    return {
      items: [],
      totalPages: 0,
      totalRows: 0,
      isLoadingTable: false,
      req: {
        filterBy: "",
        // UC17: lọc thiết bị (contains tên hoặc mã). rỗng = tất cả.
        deviceFilter: "",
        from: null,
        to: null,
        result: null, // null = tất cả
        reason: null,
        // GAP 17.1: nhóm loại sự kiện (giá trị enum khớp BE OtpEventTypeGroup).
        eventTypeGroup: null,
        PageIndex: 1,
        PageSize: 50,
      },
      resultOptions: [
        { value: null, text: "Tất cả" },
        { value: true, text: "Thành công" },
        { value: false, text: "Thất bại" },
      ],
      // GAP 17.1: 6 nhóm khớp enum BE OtpEventTypeGroup (giá trị số phải trùng).
      eventTypeGroupOptions: [
        { value: null, text: "Tất cả" },
        { value: 1, text: "Xác thực OTP" },
        { value: 2, text: "Quản lý thiết bị" },
        { value: 3, text: "Kích hoạt" },
        { value: 4, text: "Reset tài khoản" },
        { value: 5, text: "Xác thực nâng cao (step-up)" },
        { value: 6, text: "Duyệt push OTP" },
      ],
      // GAP 17.1 INFO: catalog nạp từ BE (single source). null cho tới khi API trả -> dùng fallback cứng bên dưới.
      // Fail load -> giữ nguyên hardcoded để zero-regression.
      serverCatalog: null,
      // GAP 17.1: reasonOptions lấp đầy 35 Reason - đồng bộ với BE OtpEventTypeCatalog.ExactLabels.
      // Prefix dynamic (stepup/push) KHÔNG liệt kê ở đây (dùng dropdown Nhóm loại).
      // Fallback cứng - override bởi computed reasonOptionsFinal khi serverCatalog nạp xong.
      reasonOptions: [
        { value: null, text: "Tất cả" },
        // XacThuc
        { value: "ok", text: "Xác thực OTP thành công" },
        { value: "invalid-otp", text: "Mã OTP không đúng" },
        { value: "ratelimit", text: "Vượt ngưỡng thử OTP" },
        { value: "replay", text: "Mã OTP đã sử dụng" },
        { value: "redis-unavailable", text: "Dịch vụ xác thực gián đoạn" },
        { value: "device-invalid", text: "Thiết bị không hợp lệ hoặc bị khoá" },
        { value: "clock-untrusted", text: "Đồng hồ máy chủ không tin cậy" },
        { value: "invalid-user", text: "Tài khoản không hợp lệ" },
        // QuanLyThietBi
        { value: "add-device-gate", text: "Thêm thiết bị mới" },
        { value: "set-primary", text: "Đặt thiết bị chính" },
        { value: "revoke-device", text: "Người dùng huỷ thiết bị" },
        { value: "admin-lock-device", text: "Quản trị khoá thiết bị" },
        { value: "admin-unlock-device", text: "Quản trị mở khoá thiết bị" },
        { value: "auto-lock-threshold", text: "Tự động khoá thiết bị do vượt ngưỡng" },
        // KichHoat
        { value: "activation-consumed", text: "Đã dùng mã kích hoạt" },
        { value: "invalid-activation-code", text: "Mã kích hoạt không hợp lệ" },
        { value: "enroll-ticket-redeem", text: "Đổi vé kích hoạt" },
        { value: "enroll-verify-otp", text: "Xác nhận mã OTP khi kích hoạt" },
        { value: "enroll-via-qr-ticket", text: "Kích hoạt thiết bị qua mã QR" },
        { value: "get-provisioning-secret", text: "Lấy mã bí mật được cấp" },
        { value: "otpprov:code", text: "Cấp mã kích hoạt OTP" },
        { value: "email", text: "Gửi email OTP" },
        // ResetTaiKhoan
        { value: "admin-reset-2fa", text: "Quản trị đặt lại cấu hình 2FA" },
        { value: "reactivate-2fa", text: "Kích hoạt lại 2FA" },
      ],
    };
  },
  computed: {
    // GAP 17.1 INFO: options từ BE catalog nếu có, không thì fallback cứng.
    eventTypeGroupOptionsFinal() {
      if (!this.serverCatalog || !this.serverCatalog.groups) return this.eventTypeGroupOptions;
      return [
        { value: null, text: "Tất cả" },
        ...this.serverCatalog.groups.map((g) => ({ value: g.value, text: g.label })),
      ];
    },
    reasonOptionsFinal() {
      if (!this.serverCatalog || !this.serverCatalog.exactReasons) return this.reasonOptions;
      return [
        { value: null, text: "Tất cả" },
        ...this.serverCatalog.exactReasons.map((e) => ({ value: e.reason, text: e.label })),
      ];
    },
    // Map exact reason -> label từ server (cho reasonLabel tra nhanh).
    serverExactMap() {
      if (!this.serverCatalog || !this.serverCatalog.exactReasons) return null;
      const m = {};
      this.serverCatalog.exactReasons.forEach((e) => { m[e.reason] = e.label; });
      return m;
    },
    // Prefix dynamic từ server (BE OtpEventTypeCatalog.Snapshot().Prefixes). Nguồn sự thật duy nhất:
    // prefix mới thêm ở BE tự có nhãn ở FE, KHÔNG cần deploy lại FE. Sắp specific-first (prefix dài
    // trước) vì "push-approve" là tiền tố của chính nó khi có suffix - khớp thứ tự thử của BE LabelFor.
    serverPrefixList() {
      if (!this.serverCatalog || !this.serverCatalog.prefixes) return null;
      const list = this.serverCatalog.prefixes
        .filter((p) => p && p.prefix && p.labelTemplate && p.labelTemplate !== p.prefix)
        .slice();
      list.sort((a, b) => b.prefix.length - a.prefix.length);
      return list.length ? list : null;
    },
  },
  created() {
    this.loadReasonCatalog();
    this.search();
  },
  methods: {
    // GAP 17.1 INFO: nạp catalog từ BE (single source). Fail -> giữ fallback cứng (zero-regression).
    loadReasonCatalog() {
      this.$store
        .dispatch("common/getOtpReasonCatalog")
        .then((data) => { if (data) this.serverCatalog = data; })
        .catch(() => { /* fallback cứng đã sẵn - im lặng */ });
    },
    formatDate(d) {
      return d ? moment(d).format("DD/MM/YYYY HH:mm:ss") : "";
    },
    // Render 1 nhãn prefix từ labelTemplate của BE (vd "Quản trị vô hiệu 2FA - {lyDo}",
    // "Thêm thiết bị mới (lỗi: {reason})", "Xác thực nâng cao ({action})").
    // Có suffix -> thay placeholder. Rỗng -> cắt luôn phần trang trí bao quanh placeholder
    // (" - {x}", " ({x})", " (lỗi: {x})") để khớp nhánh IsNullOrEmpty của BE LabelFor.
    applyLabelTemplate(template, suffix) {
      if (!template) return null;
      const hasPlaceholder = /\{[^}]*\}/.test(template);
      // Template KHÔNG có placeholder (push-approve/push-complete: BE trả "Duyệt push OTP" trơn)
      // nhưng LabelFor vẫn nối "({suffix})" khi reason có đuôi. Giữ đối xứng hành vi đó.
      if (!hasPlaceholder) return suffix ? `${template} (${suffix})` : template;
      if (suffix) return template.replace(/\{[^}]*\}/, suffix);
      // Bỏ đuôi trang trí: " ( ... {x} ... )" hoặc " - {x}" ở cuối chuỗi.
      return template
        .replace(/\s*\([^()]*\{[^}]*\}[^()]*\)\s*$/, "")
        .replace(/\s*-\s*\{[^}]*\}\s*$/, "")
        .replace(/\s*\{[^}]*\}\s*$/, "")
        .trim();
    },
    // GAP 17.1: nhãn Reason - đồng bộ với BE OtpEventTypeCatalog.LabelFor.
    // Ưu tiên nguồn server (exact + prefix từ endpoint /reason-catalog) để prefix/reason mới thêm ở BE
    // tự hiển thị đúng mà không phải sửa FE. Fallback map cứng chỉ dùng khi catalog chưa nạp / API fail.
    reasonLabel(reason) {
      if (!reason) return reason;
      // (1) Exact từ server.
      if (this.serverExactMap && this.serverExactMap[reason]) return this.serverExactMap[reason];
      // (2) Prefix từ server - specific-first (đã sort theo độ dài prefix giảm dần).
      if (this.serverPrefixList) {
        for (const p of this.serverPrefixList) {
          if (!reason.startsWith(p.prefix)) continue;
          const suffix = this.extractSuffix(reason, p.prefix);
          const label = this.applyLabelTemplate(p.labelTemplate, suffix);
          if (label) return label;
        }
      }
      // (3) Fallback cứng - giữ nguyên hành vi khi catalog API fail (zero-regression).
      return this.reasonLabelFallback(reason);
    },
    // Tách suffix sau prefix. Hai quy ước của BE:
    //  - prefix kết thúc ':' -> suffix = toàn bộ phần còn lại (vd "admin-disable-2fa:lý do có : trong đó").
    //  - prefix KHÔNG có ':' (push-approve/push-complete) -> literal đứng một mình được, suffix nằm sau dấu ':'.
    //  - riêng stepup-*: reason là "{prefix}{action}:{result}" -> BE chỉ lấy segment ĐẦU làm nhãn.
    extractSuffix(reason, prefix) {
      let suffix = prefix.endsWith(":")
        ? reason.substring(prefix.length)
        : (reason.length > prefix.length ? reason.substring(prefix.length + 1) : "");
      if (prefix === "stepup-verify:" || prefix === "stepup-consume:") {
        suffix = suffix.split(":")[0] || "";
      }
      return suffix;
    },
    // Fallback cứng: dùng KHI VÀ CHỈ KHI catalog server chưa nạp / lỗi. Đồng bộ thủ công với
    // BE OtpEventTypeCatalog (ExactLabels + LabelFor). Thêm reason mới ở BE thì đường (1)/(2) đã phủ;
    // bổ sung ở đây chỉ để bảng không lộ mã thô khi API catalog chết.
    reasonLabelFallback(reason) {
      const exact = {
        // XacThuc
        ok: "Xác thực OTP thành công",
        "invalid-otp": "Mã OTP không đúng",
        ratelimit: "Vượt ngưỡng thử OTP",
        replay: "Mã OTP đã sử dụng",
        "redis-unavailable": "Dịch vụ xác thực gián đoạn",
        "device-invalid": "Thiết bị không hợp lệ hoặc bị khoá",
        "device-locked": "Thiết bị bị khoá bởi quản trị",
        "device-pending": "Thiết bị đang chờ kích hoạt",
        "device-foreign": "Thiết bị thuộc tài khoản khác",
        "clock-untrusted": "Đồng hồ máy chủ không tin cậy",
        "invalid-user": "Tài khoản không hợp lệ",
        // QuanLyThietBi
        "add-device-gate": "Thêm thiết bị mới",
        "mint-add-ticket-gate": "Thêm thiết bị mới qua mã QR",
        "set-primary": "Đặt thiết bị chính",
        "revoke-device": "Người dùng huỷ thiết bị",
        "self-revoke-device": "Người dùng tự gỡ/hủy thiết bị",
        "auto-revoke-replaced": "Tự động huỷ thiết bị cũ",
        "admin-lock-device": "Quản trị khoá thiết bị",
        "admin-unlock-device": "Quản trị mở khoá thiết bị",
        "auto-lock-threshold": "Tự động khoá thiết bị do vượt ngưỡng",
        "self-unlock-device-email": "Người dùng tự mở khóa thiết bị (email)",
        // KichHoat
        "activation-consumed": "Đã dùng mã kích hoạt",
        "invalid-activation-code": "Mã kích hoạt không hợp lệ",
        "enroll-ticket-redeem": "Đổi vé kích hoạt",
        "enroll-verify-otp": "Xác nhận mã OTP khi kích hoạt",
        "enroll-via-qr-ticket": "Kích hoạt thiết bị qua mã QR",
        "get-provisioning-secret": "Lấy mã bí mật được cấp",
        "otpprov:code": "Cấp mã kích hoạt OTP",
        email: "Gửi email OTP",
        "admin-reissue-activation": "Quản trị cấp lại mã kích hoạt",
        // PushApproval
        "otp-login:invalidate-push": "Đăng nhập xác thực OTP",
        // ResetTaiKhoan
        "admin-reset-2fa": "Quản trị đặt lại cấu hình 2FA",
        "reactivate-2fa": "Kích hoạt lại 2FA",
        "admin-disable-2fa": "Quản trị vô hiệu 2FA",
        "admin-enable-2fa": "Quản trị bật lại 2FA",
        "self-revoke-last-disable-2fa": "Người dùng tự gỡ thiết bị cuối (tắt 2FA)",
        // MandateOtp
        "role-group-otp-mandate-enabled": "Bắt buộc OTP theo vai trò/nhóm",
      };
      if (exact[reason]) return exact[reason];
      // Prefix dynamic - cùng thứ tự specific-first như BE LabelFor.
      const prefixTemplates = [
        ["stepup-verify:", "Xác thực nâng cao ({action})"],
        ["stepup-consume:", "Sử dụng OTP nâng cao ({action})"],
        ["email-unverified-deny:", "Email chưa xác minh (chặn kích hoạt) - {flow}"],
        ["add-device-gate:", "Thêm thiết bị mới (lỗi: {reason})"],
        ["mint-add-ticket-gate:", "Thêm thiết bị mới qua mã QR (lỗi: {reason})"],
        ["revoke-device:", "Người dùng huỷ thiết bị (lỗi: {reason})"],
        ["self-revoke-device:", "Người dùng tự gỡ thiết bị chưa kích hoạt (lỗi: {reason})"],
        ["self-unlock-device-email:", "Người dùng tự mở khóa thiết bị (email) (lỗi: {reason})"],
        ["admin-reset-2fa:", "Quản trị đặt lại cấu hình 2FA - {lyDo}"],
        ["reactivate-2fa:", "Kích hoạt lại 2FA - {lyDo}"],
        ["admin-disable-2fa:", "Quản trị vô hiệu 2FA - {lyDo}"],
        ["admin-enable-2fa:", "Quản trị bật lại 2FA - {lyDo}"],
        // Không có ':' - phải thử SAU các prefix có ':' để không nuốt nhầm.
        ["push-approve", "Duyệt push OTP"],
        ["push-complete", "Hoàn tất push OTP"],
      ];
      for (const [prefix, template] of prefixTemplates) {
        if (!reason.startsWith(prefix)) continue;
        const label = this.applyLabelTemplate(template, this.extractSuffix(reason, prefix));
        if (label) return label;
      }
      return reason; // reason lạ chưa map - hiện thô để lộ ra mà bổ sung
    },
    payload() {
      return {
        FilterBy: this.req.filterBy,
        // UC17: gửi filter thiết bị. rỗng -> BE bỏ qua (trả tất cả).
        DeviceFilter: this.req.deviceFilter,
        From: this.req.from || null,
        To: this.req.to || null,
        Result: this.req.result,
        Reason: this.req.reason,
        // GAP 17.1: gửi group filter. null = tất cả (BE bỏ qua nhánh EventTypeGroup).
        EventTypeGroup: this.req.eventTypeGroup,
        PageIndex: this.req.PageIndex,
        PageSize: this.req.PageSize,
      };
    },
    search() {
      this.isLoadingTable = true;
      this.$store
        .dispatch("common/searchOtpAudit", this.payload())
        .then((res) => {
          this.isLoadingTable = false;
          if (res.succeeded && res.data) {
            this.items = res.data.items || [];
            this.totalRows = res.data.totalCount || 0;
            this.totalPages = res.data.totalPage || 0;
          }
        })
        .catch(() => {
          this.isLoadingTable = false;
        });
    },
    // GAP 17.3: xuất Excel (xlsx) hoặc PDF cùng filter. Format="pdf" -> BE trả .pdf; còn lại -> .xlsx.
    exportFile(format) {
      const isPdf = format === "pdf";
      const payload = { ...this.payload(), Format: format };
      this.$store
        .dispatch("common/exportOtpAudit", payload)
        .then((response) => {
          const blob = new Blob([response.data], {
            type: isPdf
              ? "application/pdf"
              : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = isPdf ? "NhatKyOTP.pdf" : "NhatKyOTP.xlsx";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch(() => {
          this.$toasted.clear();
          this.$toasted.global.showErrorMessage({
            message: isPdf ? "Xuất PDF thất bại" : "Xuất Excel thất bại",
          });
        });
    },
    onChangeNum(pageSize) {
      this.req.PageIndex = 1;
      this.req.PageSize = pageSize;
      this.search();
    },
    onChangePages(page) {
      this.req.PageIndex = page;
      this.search();
    },
  },
};
</script>

<style lang="scss" scoped>
// Reskin OTP (Phase 2): overlay token --otp-* + typography + chip kết quả + số mono. Logic/filter giữ nguyên.
.nhat-ky-otp {
  font-family: var(--otp-sans);
  color: var(--otp-ink);

  .otp-page-head {
    margin-bottom: 18px;
  }
  .otp-page-title {
    font-family: var(--otp-display);
    font-weight: 600;
    font-size: 20px;
    color: var(--otp-ink);
    margin: 0 0 6px;
  }
  .otp-page-sub {
    color: var(--otp-muted);
    font-size: 13.5px;
    margin: 0;
  }

  // Filter bar: nền surface-2 gọn, nhãn nhỏ đậm.
  .otp-filter-bar {
    background: var(--otp-surface-2);
    border: 1px solid var(--otp-border);
    border-radius: var(--otp-radius);
    padding: 16px 16px 6px;
    margin-bottom: 16px;
    label {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--otp-ink-soft);
      margin-bottom: 4px;
    }
  }

  // Chip kết quả Thành công/Thất bại: nền + chấm + chữ (màu+icon+chữ đạt WCAG).
  .otp-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.6;
    .otp-chip__dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: currentColor;
      flex: none;
    }
    &--on {
      background: var(--otp-success-bg);
      color: var(--otp-success);
    }
    &--off {
      background: var(--otp-danger-bg);
      color: var(--otp-danger);
    }
  }

  // Số IP/thời gian: font mono tabular cho thẳng cột.
  .otp-mono {
    font-family: var(--otp-mono);
    font-variant-numeric: tabular-nums;
    font-size: 12.5px;
    color: var(--otp-ink-soft);
  }
}
</style>
